import { storage } from './storage';
import { STORAGE_KEYS } from '../constants/storageKeys';
import { logger } from './logger';
import { createLogContext } from './loggerUtils';
import { checkFeatureEnabled } from './features';
import { getPlatform } from '../infrastructure/platform';
import type { Prompt, Folder } from '../types';

/**
 * Manages automatic weekly backups of user prompts
 * Creates backups in the user's downloads directory
 * Saves backups with date-based filenames in the TopPromptBackups folder
 * This is a premium feature that must be enabled in settings
 */
export class BackupManager {
  private static BACKUP_INTERVAL = 7 * 24 * 60 * 60 * 1000; // 7 days in milliseconds
  private static BACKUP_KEY = 'last_backup_timestamp';
  private static BACKUP_DIR = 'TopPromptBackups';

  /**
   * Checks if a backup is needed and creates one if necessary
   * Should be called when the app is initialized or after significant changes
   */
  static async checkAndCreateBackup(): Promise<void> {
    try {
      // Check if weekly backup feature is enabled (premium feature)
      const isEnabled = await checkFeatureEnabled('weeklyBackup');
      if (!isEnabled) {
        logger.debug('Weekly backup feature is not enabled', {
          module: 'BackupManager',
          function: 'checkAndCreateBackup',
        });
        return;
      }

      const ctx = createLogContext('checkAndCreateBackup');
      const lastBackup = (await storage.get(this.BACKUP_KEY)) || 0;
      const now = Date.now();

      logger.debug('Checking if backup is needed', {
        ...ctx,
        lastBackup: new Date(lastBackup).toISOString(),
        now: new Date(now).toISOString(),
        interval: this.BACKUP_INTERVAL,
        timeSinceLastBackup: now - lastBackup,
      });

      if (now - lastBackup >= this.BACKUP_INTERVAL) {
        logger.info('Backup interval exceeded, creating backup', ctx);
        await this.createBackup();
        await storage.set(this.BACKUP_KEY, now);
      } else {
        logger.debug('Backup not needed yet', ctx);
      }
    } catch (error) {
      logger.error(
        'Failed to check or create backup',
        error instanceof Error ? error : new Error('Unknown error'),
        {
          module: 'BackupManager',
          function: 'checkAndCreateBackup',
        }
      );
    }
  }

  /**
   * Creates a backup of user prompts and folders
   * The backup is saved to the TopPromptBackups folder with a date-based filename
   */
  private static async createBackup(): Promise<void> {
    try {
      const ctx = createLogContext('createBackup');

      // Get prompts and folders
      const prompts = ((await storage.get(STORAGE_KEYS.PROMPTS)) as Prompt[]) || [];
      const folders = ((await storage.get(STORAGE_KEYS.FOLDERS)) as Folder[]) || [];

      // Skip if no prompts to backup
      if (prompts.length === 0) {
        logger.info('No prompts to backup, skipping', ctx);
        return;
      }

      // Create export data (similar to DataTab.tsx export function)
      const exportData = prompts.map((prompt: Prompt) => ({
        id: prompt.id,
        title: prompt.title,
        text: prompt.text,
        description: prompt.description,
        tags: prompt.tags,
        folder: folders.find((f: Folder) => f.id === prompt.folder)?.name || '',
        lastChanged: prompt.lastChanged,
      }));

      // Create filename with current date
      const date = new Date();
      const dateStr = date.toISOString().split('T')[0]; // YYYY-MM-DD
      const filename = `TopPromptLibrary_Backup_${dateStr}.json`;

      // Write backup file
      await this.writeBackupFile(filename, exportData);

      logger.info(`Backup created successfully: ${filename}`, {
        ...ctx,
        promptCount: prompts.length,
        folderCount: folders.length,
      });
    } catch (error) {
      logger.error(
        'Backup creation failed',
        error instanceof Error ? error : new Error('Unknown error'),
        {
          module: 'BackupManager',
          function: 'createBackup',
        }
      );
    }
  }

  /**
   * Writes a backup file to the downloads directory
   * Uses a silent download approach to minimize UI impact
   */
  private static async writeBackupFile(filename: string, data: any): Promise<void> {
    try {
      const ctx = createLogContext('writeBackupFile', { filename });

      // Create a Blob with the JSON data
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });

      const platform = getPlatform();
      if (platform.capabilities.fileDownloads) {
        try {
          const downloadId = await platform.actions.downloadFile({
            blob,
            filename,
            subdirectory: this.BACKUP_DIR,
          });
          logger.info(`Backup file downloaded with ID: ${downloadId}`, ctx);
        } catch (downloadError) {
          logger.error(
            'Failed to download backup file',
            downloadError instanceof Error ? downloadError : new Error(String(downloadError)),
            ctx
          );
        }
      } else {
        logger.warning('File downloads API not available, cannot save backup', ctx);
      }
    } catch (error) {
      logger.error(
        'Failed to write backup file',
        error instanceof Error ? error : new Error('Unknown error'),
        {
          module: 'BackupManager',
          function: 'writeBackupFile',
          filename,
        }
      );
      throw error;
    }
  }
}
